import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { PageMeta } from '@/components/PageMeta';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { TryItNowWidget } from '@/components/TryItNowWidget';
import {
  Sparkles, Camera, FileText, Video, Globe, Layers, Zap,
  CheckCircle2, ArrowRight, Crown, Wrench, Clock,
} from 'lucide-react';

const faqs = [
  {
    q: 'What are the best real estate marketing tools in 2026?',
    a: 'The most useful tools combine listing photos, descriptions, video, and a shareable microsite in one place. ListBright bundles all of these so you skip the multi-tool stack (Canva + ChatGPT + virtual stager + flyer maker) for a single workflow.',
  },
  {
    q: 'Are there free real estate marketing tools?',
    a: 'Yes — ListBright includes a free demo (3 photo enhancements + a sample description). For full listings, paid plans start at $15 for a single property.',
  },
  {
    q: 'What marketing tools do top real estate agents use?',
    a: 'Top agents typically pair a CRM (Follow Up Boss, kvCORE) with a content stack — photo enhancement, AI descriptions, video, and social-ready exports. ListBright covers the content stack end-to-end.',
  },
  {
    q: 'How much do real estate marketing tools cost?',
    a: 'Standalone tools add up: virtual staging $20–30/photo, copywriting $50–150/listing, video editing $100+. ListBright replaces all three for $15 per listing or $/€ subscription plans.',
  },
  {
    q: 'Do I need separate tools for photos, descriptions, and video?',
    a: 'No. Modern AI marketing platforms generate all three from a single upload. ListBright produces enhanced photos, 5 description formats, a cinematic video, and a microsite in about 10 minutes.',
  },
];

const tools = [
  { icon: Camera, title: 'AI Photo Enhancement', desc: 'Declutter, golden hour, sky replace, virtual staging — 10 rules applied automatically.' },
  { icon: FileText, title: 'AI Listing Descriptions', desc: 'Facebook, Instagram, Stories, Marketplace, Premium — generated in parallel.' },
  { icon: Video, title: 'Cinematic Slideshow Video', desc: 'Listing video built from enhanced photos with music and motion.' },
  { icon: Globe, title: 'Shareable Microsite', desc: 'A clean property page you can send by SMS, email, or QR code.' },
  { icon: Layers, title: 'PDF Flyers', desc: 'One-page flyer in 14 languages, ready to print or share.' },
  { icon: Zap, title: '16-Language Translation', desc: 'Every description saved in 16 languages — switch markets in one click.' },
];

export default function RealEstateMarketingTools() {
  const navigate = useNavigate();

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'ListBright Real Estate Marketing Tools',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: 'https://listbright.ai/real-estate-marketing-tools',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
      description: 'All-in-one AI real estate marketing tools: photo enhancement, listing descriptions, slideshow video, microsite, and PDF flyers.',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question', name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <Layout>
      <PageMeta
        title="Real Estate Marketing Tools — The All-in-One AI Listing Suite"
        description="The all-in-one AI suite for real estate listings: photos, virtual staging, descriptions, video, microsite and flyer — every asset a listing needs, from a single upload."
        canonical="https://listbright.ai/real-estate-marketing-tools"
        breadcrumbs={[{ label: 'Real Estate Marketing Tools', path: '/real-estate-marketing-tools' }]}
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden py-20 md:py-28">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="container max-w-5xl mx-auto px-4 relative text-center space-y-6">
          <Badge variant="outline" className="px-4 py-1.5 text-sm">
            <Wrench className="w-3.5 h-3.5 mr-1.5" /> Marketing Suite
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
            The All-in-One <span className="text-primary">AI Suite</span> for Real Estate Listings
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Photos, virtual staging, descriptions, video, microsite and flyer — every asset a
            listing needs, generated from a single upload. Stop juggling Canva, ChatGPT, a virtual
            stager and a video editor; ListBright replaces the whole stack in one 10-minute workflow.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> Free demo</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> No credit card</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-primary" /> Cancel anytime</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            <Button asChild size="lg"><a href="#try">Try Free <ArrowRight className="w-4 h-4 ml-2" /></a></Button>
            <Button asChild size="lg" variant="outline"><Link to="/pricing">See Pricing</Link></Button>
          </div>
        </div>
      </section>

      <section id="try" className="py-16 bg-muted/30">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Try the Tools — Free</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Drop a property photo. See enhancement, description, and styling in seconds. No signup.
            </p>
          </div>
          <TryItNowWidget onGetStarted={() => navigate('/auth')} />
        </div>
      </section>

      <section className="py-20">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Every Tool a Listing Needs</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Six tools, one upload. No exporting between apps.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map(({ icon: Icon, title, desc }) => (
              <Card key={title} className="border-border/50">
                <CardContent className="p-6 space-y-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container max-w-4xl mx-auto px-4 text-center space-y-6">
          <Badge className="mb-2"><Crown className="w-3.5 h-3.5 mr-1.5" /> All-In-One</Badge>
          <h2 className="text-3xl md:text-4xl font-bold">
            One Subscription Replaces a $200/Month Stack
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Most agents pay for Canva, ChatGPT Plus, a virtual stager, a video editor, and a flyer
            tool separately. ListBright covers all of it — and produces a finished, marketplace-ready
            listing in 10 minutes.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
            <Button asChild size="lg">
              <Link to="/single-property-kit">
                Single Property Kit – $15 <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/pricing">See All Plans</Link>
            </Button>
          </div>
          <p className="text-sm text-muted-foreground flex items-center justify-center gap-1.5 pt-2">
            <Clock className="w-4 h-4" /> Done in 10 minutes. Sold in half the time.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="container max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((f) => (
              <Card key={f.q}>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-2">{f.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{f.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button asChild size="lg">
              <a href="#try">Try the Suite Free <ArrowRight className="w-4 h-4 ml-2" /></a>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}